import { AlertIcon, QBooksDisabledLogo, QBooksLogo, XeroDisabledLogo, XeroLogo } from '@approvalmax/ui';
import { Alert, Flex, Progress, Spacing, Text } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { domain } from 'modules/data';
import { FC, memo, useMemo } from 'react';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { useIntegrationTypeDecision } from '../../hooks/useIntegrationTypeDecision';
import { syncAllBudgetsPageState } from '../../states';
import { messages } from './BudgetTile.messages';
import { Overlay, ProgressWrapper, Tile } from './BudgetTile.styles';
import { BudgetTileProps } from './BudgetTile.types';

export const BudgetTile: FC<BudgetTileProps> = memo((props) => {
    const { isEnabled, name, origin, isSyncInProgress, isSyncFailed, actionsMenu, budgetParams } = props;

    const syncAllBudgetsState = useAtomValue(syncAllBudgetsPageState);
    const company = useCompanyBasedOnUrl();
    const integrationTypeDecision = useIntegrationTypeDecision();
    const { isManager, isAuditor, hasActiveIntegration } = company.flags;
    const budgetDisabled = !hasActiveIntegration || !isEnabled;
    const showActionsMenu = isManager || (isAuditor && origin === domain.BudgetOrigin.Manual);

    const Logo = useMemo(() => {
        if (origin === domain.BudgetOrigin.Manual) return null;

        switch (integrationTypeDecision) {
            case domain.IntegrationType.Xero:
                return budgetDisabled ? XeroDisabledLogo : XeroLogo;

            case domain.IntegrationType.QBooks:
                return budgetDisabled ? QBooksDisabledLogo : QBooksLogo;

            default:
                return null;
        }
    }, [origin, integrationTypeDecision, budgetDisabled]);

    const isLoading = isSyncInProgress || syncAllBudgetsState;

    return (
        <Flex size={8}>
            <Tile $disabled={budgetDisabled} $progress={isLoading}>
                {isSyncFailed && (
                    <>
                        <Alert color='midnight20' size='small' startIcon={<AlertIcon />} invalid={!budgetDisabled}>
                            {messages.synchronizationFailed}
                        </Alert>

                        <Spacing height={16} />
                    </>
                )}

                <Flex wrap={false} justifyContent='space-between' alignItems='start' spacing='8'>
                    <Flex spacing='8' wrap={false}>
                        {Logo && (
                            <Flex shrink={0}>
                                <Logo size={24} />
                            </Flex>
                        )}

                        <Text
                            font='headline'
                            fontSize='xsmall'
                            ellipsis={2}
                            color={budgetDisabled ? 'midnight70' : undefined}
                            as='h3'
                        >
                            {name}
                        </Text>
                    </Flex>

                    {isLoading && (
                        <Flex shrink={0}>
                            <ProgressWrapper>
                                <Progress size='xsmall' shape='circle' />
                            </ProgressWrapper>
                        </Flex>
                    )}

                    {showActionsMenu && actionsMenu}
                </Flex>

                <Spacing height={16} />

                {budgetParams}

                {isLoading && <Overlay />}
            </Tile>
        </Flex>
    );
});

BudgetTile.displayName = 'BudgetTile';
